import { useState, ReactNode, useEffect } from 'react';
import { Link } from '@heroui/react';
import { LuMaximize, LuMinimize } from 'react-icons/lu';
import { useAppSelector } from '../../../core/rootHooks';
import { selectSelectionData } from '../../../features/search/searchSelectors';
import { MAIN_PANEL_OFFSET } from '../../../constants/constants';

interface ExpandableContainerProps {
	children: ReactNode;
	title?: string;
	titleClassName?: string;
	expandable?: boolean;
	panelOffsets?: { top: number; left: number };
	hideTitleBar?: boolean;
	reverseIconColor?: boolean;
}

/**
 * Expandable container component that conditionally renders its children
 * in a fixed full-screen container when expanded.
 *
 * @param {ReactNode} children - The content of the container.
 * @param {string} [title] - The title of the container.
 * @param {string} [titleClassName] - The class name for the title.
 * @param {boolean} [expandable=true] - Whether the container is expandable.
 * @param {boolean} [hideTitleBar=false] - Whether to hide the title bar when expanded.
 * @param {boolean} [reverseIconColor=false] - Whether to reverse the icon color when expanded.
 * @param {{ top: number, left: number }} [panelOffsets={ top: 0, left: 0 }] - The offset of the expanded container from the top-left corner of the main panel.
 */
const ExpandableContainer: React.FC<ExpandableContainerProps> = ({
	children,
	title,
	titleClassName,
	expandable = true,
	panelOffsets = { top: 0, left: 0 },
	hideTitleBar = false,
	reverseIconColor = false,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [expandedStyle, setExpandedStyle] = useState({});

	const selectionData = useAppSelector(selectSelectionData);

	// Reset the expanded state when the selection data changes
	useEffect(() => {
		if (selectionData) {
			setIsExpanded(false);
		}
	}, [selectionData]);

	useEffect(() => {
		if (isExpanded) {
			// Set the expanded style with the content's dimensions
			setExpandedStyle({
				top: MAIN_PANEL_OFFSET.top + panelOffsets.top,
				left: MAIN_PANEL_OFFSET.left + panelOffsets.left - ((panelOffsets.left > 0 && 12) || 0),
				right: MAIN_PANEL_OFFSET.right,
				bottom: MAIN_PANEL_OFFSET.bottom,
			});
		} else {
			// Reset the expanded style when not expanded
			setExpandedStyle({});
		}
	}, [isExpanded]);

	return (
		<div className="relative flex flex-col w-full h-full">
			{/*  Container for the content that we'll measure */}
			{!isExpanded && (
				<div className="flex flex-col w-full h-full">
					{!hideTitleBar && (
						<div className="h-6">{title && <p className={`pl-1 font-semibold ${titleClassName || ''}`}>{title}&nbsp;</p>}</div>
					)}
					<div className="flex flex-col w-full h-full">{children}</div>
				</div>
			)}

			{expandable && (
				<Link
					className="absolute cursor-pointer"
					style={{ right: 20, top: 10 }}
					aria-label="Maximize"
					title="Maximize"
					onPress={() => setIsExpanded(true)}
				>
					<LuMaximize
						color={reverseIconColor ? '#FFFFFF' : '#151B1E'}
						size={16}
						className='rounded-none absolute'
					/>
				</Link>
			)}

			{/*  Conditionally render the expanded view */}
			{isExpanded && (
				<div style={expandedStyle} className="fixed z-50 bg-msi-white-color ml-1 mr-1 mb-1">
					<div className="flex flex-col w-full h-full">
						{!hideTitleBar && (
							<div className="h-6">{title && <p className={`pl-1 font-semibold ${titleClassName || ''}`}>{title}&nbsp;</p>}</div>
						)}
						<div className="flex grow flex-col w-full h-full">{children}</div>

						<Link
							className="absolute cursor-pointer"
							style={{ right: 20, top: 10 }}
							aria-label="Minimize"
							title="Minimize"
							onPress={() => setIsExpanded(false)}
						>
							<LuMinimize
								color={reverseIconColor ? '#FFFFFF' : '#151B1E'}
								size={16}
								className='rounded-none absolute'								
							/>
						</Link>
					</div>
				</div>
			)}
		</div>
	);
};

export default ExpandableContainer;
